import type { Table } from '@tanstack/react-table';
import { useCallback, useState } from 'react';

/**
 * A hook that provides a table state and callback for TanStackTable.
 *
 * Unlike a regular useRef, this hook causes the component to re-render when
 * the table instance becomes available, ensuring dependent components
 * (like CSV/Print buttons) can access the table data.
 *
 * @returns A tuple of [table, onTableReady] where:
 *   - table: The current table instance (null until set)
 *   - onTableReady: A callback to pass to TanStackTable's onTableReady prop
 */
export function useTableRef<T>(): [Table<T> | null, (table: Table<T>) => void] {
  const [table, setTable] = useState<Table<T> | null>(null);

  const onTableReady = useCallback((instance: Table<T>) => {
    setTable(instance);
  }, []);

  return [table, onTableReady];
}
